import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isIco, personResultStyle } from '../styles';
import { useAuth } from '../auth/AuthContext';

interface CompanySearchHit {
  ico: string;
  name: string;
  city: string | null;
}

// Perzistentná horná lišta — vyhľadávanie podľa IČO (priama navigácia) alebo názvu.
// Návrhy sa teraz naťahujú priebežne počas písania (debounce 250ms), nie až po
// odoslaní formulára — search-endpoint beží od migrácie 0020 nad lokálnym
// trigram indexom, takže znesie volanie na každý stlačený kláves. Zdieľaná
// medzi HomePage a CompanyPage.
export function TopBar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (!q || isIco(q)) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: results } = useQuery<CompanySearchHit[]>({
    queryKey: ['company-search', debounced],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/search?q=${encodeURIComponent(debounced)}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: debounced.length >= 2,
  });

  function submit(): void {
    const q = query.trim();
    if (!q) return;
    if (isIco(q)) {
      navigate(`/firma/${q}`);
      setQuery('');
    } else {
      setDebounced(q);
    }
  }

  function closeDropdown(): void {
    setQuery('');
    setDebounced('');
  }

  const searchForm = (
    <div style={{ position: 'relative', flex: 1 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
          setMobileOpen(false);
        }}
        style={{ display: 'flex', gap: '0.4rem' }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="IČO alebo názov firmy"
          style={{
            flex: 1,
            padding: '0.5rem 0.7rem',
            border: '1px solid var(--chrome-line)',
            borderRadius: 8,
            fontSize: '0.9rem',
            background: 'var(--chrome-field)',
            color: 'var(--chrome-ink)',
          }}
        />
        <button type="submit" style={searchButtonStyle}>
          Hľadať
        </button>
      </form>
      {results && debounced.length >= 2 && (
        <div style={dropdownStyle}>
          {results.length === 0 && (
            <div style={{ padding: '0.6rem', color: 'var(--slate)', fontSize: '0.85rem' }}>
              Žiadna firma nenájdená.
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.ico}
              type="button"
              onClick={() => {
                navigate(`/firma/${c.ico}`);
                closeDropdown();
                setMobileOpen(false);
              }}
              style={personResultStyle}
            >
              {c.name}
              <span className="num" style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                {' '}
                · IČO {c.ico}
                {c.city ? ` · ${c.city}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const navLinks = (
    <>
      <Link to="/vyhladavanie" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Vyhľadávanie
      </Link>
      <Link to="/osoby" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Vlastníci
      </Link>
      <Link to="/insolvencie" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Insolvencie
      </Link>
      <Link to="/drazby" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Dražby
      </Link>
      <Link to="/zmluvy" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Zmluvy
      </Link>
      <Link to="/inzeraty" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Trhovisko
      </Link>
      <Link to="/nastroje" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Analýzy
      </Link>
      <Link to="/cennik" className="topbar-nav-link" style={navLinkStyle} onClick={() => setMobileOpen(false)}>
        Cenník
      </Link>
    </>
  );

  const accountLink = user ? (
    <Link to="/ucet" style={accountPillStyle} onClick={() => setMobileOpen(false)}>
      {user.email}
      {user.plan === 'pro' ? ' · Pro' : ''}
    </Link>
  ) : (
    <Link to="/prihlasenie" style={accountPillStyle} onClick={() => setMobileOpen(false)}>
      Prihlásiť sa
    </Link>
  );

  return (
    <>
      <header className="topbar" style={headerStyle}>
        <Link to="/" style={brandStyle}>
          <svg width="26" height="26" viewBox="0 0 34 34" aria-hidden="true">
            <line x1="8" y1="26" x2="24" y2="9" stroke="var(--chrome-gold)" strokeWidth="1.6" />
            <line
              x1="24"
              y1="9"
              x2="28"
              y2="24"
              stroke="currentColor"
              strokeWidth="1.2"
              opacity="0.5"
            />
            <circle cx="8" cy="26" r="4" fill="currentColor" opacity="0.92" />
            <circle cx="24" cy="9" r="5.2" fill="var(--chrome-gold)" />
            <circle cx="28" cy="24" r="3" fill="currentColor" opacity="0.55" />
          </svg>
          <span style={{ lineHeight: 1 }}>
            corpuls
            <span style={tagStyle}>Analytika</span>
          </span>
        </Link>
        <div className="topbar-search-desktop" style={{ flex: 1, maxWidth: 420 }}>
          {searchForm}
        </div>
        <nav className="topbar-nav-desktop" style={navStyle}>
          {navLinks}
        </nav>
        <div className="topbar-account-desktop">{accountLink}</div>
        <button
          type="button"
          className="topbar-burger"
          aria-label={mobileOpen ? 'Zavrieť menu' : 'Otvoriť menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      </header>
      {mobileOpen && (
        <div className="topbar-mobile-panel">
          {searchForm}
          <nav className="topbar-mobile-nav">{navLinks}</nav>
          {accountLink}
        </div>
      )}
    </>
  );
}

// Rebrand 2026-07-18 (v2/v3 Artifact mockupy) — navy chrome lišta so zlatým
// "puls" v logu; tmavá v oboch témach (--chrome-* tokeny v theme.css).
const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1.5rem',
  padding: '0.85rem 1.5rem',
  background: 'var(--chrome-bg)',
  borderBottom: '1px solid var(--chrome-line)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};
// E-Analytika logo: konštelácia (medený uzol = "zistenie") + lowercase wordmark,
// ANALYTIKA medenou pod ním namiesto tagu v rámčeku.
const brandStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontFamily: 'var(--sans)',
  fontWeight: 800,
  fontSize: '1.1rem',
  letterSpacing: '-0.02em',
  color: 'var(--chrome-ink)',
  whiteSpace: 'nowrap',
};
const tagStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.56rem',
  letterSpacing: '0.34em',
  textTransform: 'uppercase',
  color: 'var(--chrome-gold)',
  fontWeight: 600,
  marginTop: 2,
};
const searchButtonStyle: CSSProperties = {
  padding: '0.5rem 1.1rem',
  border: 'none',
  background: 'var(--brand)',
  color: '#ffffff',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '0.4rem',
  background: 'var(--bg-raised)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-md)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 20,
};
const navStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: '1.2rem',
  fontSize: '0.86rem',
};
const navLinkStyle: CSSProperties = { color: 'var(--chrome-muted)' };
const accountPillStyle: CSSProperties = {
  color: 'var(--chrome-ink)',
  whiteSpace: 'nowrap',
  fontSize: '0.86rem',
  fontWeight: 500,
  padding: '0.4rem 0.9rem',
  borderRadius: 8,
  border: '1px solid var(--brand)',
  background: 'transparent',
};
